/**
 * MKS-406 — admin page for the GLOBAL task labels shared by every project.
 *
 * System labels (robot-candidate, no-robot, sre*, Bug, Feature) are looked up by
 * name by the Robot and the SRE agent, so they cannot be renamed or deleted here;
 * their colour can still change.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGlobalTaskLabels,
  useCreateGlobalTaskLabel,
  useUpdateTaskLabel,
  useDeleteTaskLabel,
} from '../../hooks/useTaskLabels';
import type { GlobalTaskLabelRecord } from '../../services/api/taskLabels';
import { Button } from '../../components/common/Button';

const DEFAULT_COLOR = '#2563EB';

export default function TaskLabelsAdminPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useGlobalTaskLabels();
  const createMutation = useCreateGlobalTaskLabel();
  const updateMutation = useUpdateTaskLabel();
  const deleteMutation = useDeleteTaskLabel();

  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState(DEFAULT_COLOR);

  const labels = data ?? [];

  const startEdit = (label: GlobalTaskLabelRecord) => {
    setEditId(label.id);
    setEditName(label.name);
    setEditColor(label.color);
    updateMutation.reset();
  };

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    createMutation.mutate(
      { name, color: newColor },
      {
        onSuccess: () => {
          setNewName('');
          setNewColor(DEFAULT_COLOR);
        },
      },
    );
  };

  const handleSave = (label: GlobalTaskLabelRecord) => {
    // Send only what changed; a system label never sends a name.
    const patch: { name?: string; color?: string } = {};
    const name = editName.trim();
    if (!label.system && name !== label.name) patch.name = name;
    if (editColor !== label.color) patch.color = editColor;
    if (Object.keys(patch).length === 0) {
      setEditId(null);
      return;
    }
    updateMutation.mutate(
      { labelId: label.id, patch },
      { onSuccess: () => setEditId(null) },
    );
  };

  const handleDelete = (label: GlobalTaskLabelRecord) => {
    if (
      confirm(`Delete "${label.name}"? It is removed from every task that carries it.`)
    ) {
      deleteMutation.mutate(label.id);
    }
  };

  const errors = [createMutation.error, updateMutation.error, deleteMutation.error].filter(
    (e): e is Error => e != null,
  );

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/admin')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Admin
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Task Labels</h1>
        <p className="text-sm text-gray-500">
          These labels are shared by every project. System labels are used by the Robot and SRE by
          name, so they cannot be renamed or deleted; their colour can still change.
        </p>
      </div>

      {errors.map((err, i) => (
        <p key={i} className="text-sm text-red-600" role="alert" data-testid="task-labels-error">
          {err.message}
        </p>
      ))}

      <div className="flex items-center gap-2" data-testid="task-label-create-row">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New label name"
          data-testid="new-label-name"
          className="rounded border border-gray-300 px-2 py-1 text-sm"
        />
        <input
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          data-testid="new-label-color"
          aria-label="New label colour"
          className="h-8 w-10 rounded border border-gray-300"
        />
        <Button
          size="sm"
          onClick={handleCreate}
          isLoading={createMutation.isPending}
          disabled={!newName.trim()}
          data-testid="new-label-create"
        >
          Create
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Label</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading && (
              <tr>
                <td colSpan={2} className="px-6 py-4 text-sm text-gray-400">Loading...</td>
              </tr>
            )}
            {labels.map((label) => (
              <tr key={label.id} data-testid={`label-row-${label.id}`}>
                {editId === label.id ? (
                  <>
                    <td className="px-6 py-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editColor}
                          onChange={(e) => setEditColor(e.target.value)}
                          data-testid={`label-color-${label.id}`}
                          aria-label="Label colour"
                          className="h-8 w-10 rounded border border-gray-300"
                        />
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          disabled={label.system}
                          title={label.system ? 'System labels cannot be renamed' : undefined}
                          data-testid={`label-name-${label.id}`}
                          className="rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100"
                        />
                        {label.system && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">System</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-2 text-right space-x-2">
                      <Button
                        size="sm"
                        onClick={() => handleSave(label)}
                        isLoading={updateMutation.isPending}
                        data-testid={`label-save-${label.id}`}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditId(null)}>
                        Cancel
                      </Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-3 text-sm text-gray-900">
                      <span className="flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          data-testid={`label-swatch-${label.id}`}
                          className="inline-block h-3 w-3 rounded-full"
                          style={{ backgroundColor: label.color }}
                        />
                        {label.name}
                        {label.system && (
                          <span
                            data-testid={`label-system-${label.id}`}
                            className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                          >
                            System
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right space-x-2">
                      <button
                        onClick={() => startEdit(label)}
                        data-testid={`label-edit-${label.id}`}
                        className="text-sm text-primary-600 hover:text-primary-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(label)}
                        disabled={label.system}
                        title={
                          label.system
                            ? 'System labels are used by the Robot/SRE by name and cannot be deleted'
                            : undefined
                        }
                        data-testid={`label-delete-${label.id}`}
                        className="text-sm text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Delete
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
